import { CopyableGuid } from "@components/CopyableText";
import { ShimmeredDetailsList } from "@components/tables/ShimmeredDetailsList";
import { withPermissions } from "@components/withPermissions";
import { FunctionComponent } from "react";

import { IColumn, useTheme } from "@bps/fluent-ui";
import { DATE_FORMATS } from "@bps/date-time";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { MessageCreditsEvent } from "@libs/api/gateways/plt-comms/plt-comms-gateway.dtos";
import { useMessageCreditsEvents } from "@libs/api/gateways/plt-comms/plt-comms-gateway.hooks";
import { Guid } from "@libs/common/guid";
import { Permissions } from "@libs/permissions/permissions.enum";

interface Props {
  tenantId: string;
}

const MessageCreditsEventsTableBase: FunctionComponent<Props> = ({
  tenantId
}) => {
  const theme = useTheme();
  const eventsQuery = useMessageCreditsEvents(tenantId);

  const columns: IColumn[] = [
    {
      key: "createdDate",
      name: "Created Date",
      onRender: (item: MessageCreditsEvent) =>
        item.changeLog.createdDate?.toFormat(
          DATE_FORMATS.LONG_DATE_FORMAT_MONTH_AHEAD_CUT
        ),
      minWidth: 150,
      maxWidth: 150
    },
    {
      key: "createdBy",
      name: "Created By",
      onRender: (item: MessageCreditsEvent) => item.changeLog.createdBy,
      minWidth: 150,
      maxWidth: 300
    },
    {
      key: "units",
      name: "Units",
      onRender: (item: MessageCreditsEvent) => item.units,
      minWidth: 80,
      maxWidth: 100
    },
    {
      key: "source",
      name: "Source",
      onRender: (item: MessageCreditsEvent) => item.source,
      minWidth: 150,
      maxWidth: 350
    },
    {
      key: "details",
      name: "Details",
      onRender: (item: MessageCreditsEvent) => item.details,
      minWidth: 300,
      maxWidth: 350
    },
    {
      key: "eventId",
      name: "Event Id",
      onRender: (item: MessageCreditsEvent) => {
        return (
          <CopyableGuid
            value={item.id ?? Guid.empty}
            variant="small"
            color={theme.palette.themeSecondary}
          />
        );
      },
      minWidth: 300,
      maxWidth: 350
    }
  ];

  return (
    <QueryStateIndicator {...eventsQuery}>
      {data => {
        return <ShimmeredDetailsList items={data} columns={columns} />;
      }}
    </QueryStateIndicator>
  );
};

export const MessageCreditsEventsTable = withPermissions(
  MessageCreditsEventsTableBase,
  [Permissions.PltCommsCreditsRead, Permissions.PltCommsCreditsWrite],
  "or"
);
